import { Grid } from '@mui/material';
import { includes } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import {
  studyHasPostProcessingResults,
  studyHasUploadedSlides,
} from 'components/StudyDashboard/studyActionAvailability';
import {
  globalSettingsEditingPermissions,
  globalSettingsViewingPermissions,
  Permission,
} from 'interfaces/permissionOption';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import { AssignmentListToggle } from './AssignmentListToggle';
import ResultsModeDropdown from './ResultsModeDropdown';
import { UserMenu } from './UserMenu';

interface Props {
  name: string;
  controlGap?: number;
  resultsModeIconOnly?: boolean;
  userMenuIconOnly?: boolean;
  userMenuMaxWidth?: number;
}

const ControlPanel: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  name,
  controlGap = 1,
  resultsModeIconOnly = false,
  userMenuIconOnly = false,
  userMenuMaxWidth,
}) => {
  const { hasPermission, hasSomePermissions } = usePermissions();
  const isAccessionViewer = hasPermission(Permission.ViewAccessionDashboard);
  const { queryParams } = useEncodedFilters();
  const { studyId: routeStudyId } = useParams();
  const hasSpecificStudyFilter =
    queryParams.filters?.studyId !== undefined &&
    !includes(NON_SPECIFIC_STUDY_VALUES, String(queryParams.filters.studyId));
  const isInStudyContext = Boolean(routeStudyId || hasSpecificStudyFilter);
  const selectedStudyId = routeStudyId || (hasSpecificStudyFilter ? queryParams.filters?.studyId : undefined);
  const { study } = useStudy(selectedStudyId, { enabled: isInStudyContext, withStatistics: true });
  const shouldShowResultsMode = !study || (studyHasUploadedSlides(study) && studyHasPostProcessingResults(study));

  const [canViewAnnotationsList] = useQueryParam<boolean>('showAssignmentsList', BooleanParam);

  const canViewOrEditSomeGlobalSettings =
    hasSomePermissions(globalSettingsEditingPermissions) || hasSomePermissions(globalSettingsViewingPermissions);

  return (
    <Grid
      item
      container
      alignItems="center"
      justifyContent="end"
      wrap="nowrap"
      columnGap={controlGap}
      sx={{ width: 'auto', minWidth: 0, flex: '1 1 auto' }}
    >
      {!isAccessionViewer && (
        <Grid
          item
          container
          alignItems="center"
          justifyContent="end"
          wrap="nowrap"
          columnGap={controlGap}
          sx={{ width: 'auto', minWidth: 0 }}
        >
          {hasPermission(Permission.ViewUnpublishedResults) && isInStudyContext && shouldShowResultsMode && (
            <Grid item>
              <ResultsModeDropdown iconOnly={resultsModeIconOnly} />
            </Grid>
          )}
          {canViewAnnotationsList && (
            <Grid item>
              <AssignmentListToggle />
            </Grid>
          )}
        </Grid>
      )}
      <Grid item sx={{ minWidth: 0, flex: '1 1 auto', display: 'flex', justifyContent: 'flex-end' }}>
        <UserMenu
          name={name}
          iconOnly={userMenuIconOnly}
          maxWidth={userMenuMaxWidth}
          canViewOrEditSomeGlobalSettings={canViewOrEditSomeGlobalSettings}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state: any) => ({
  name: state.auth.profile && state.auth.profile.name,
});

export default connect(mapStateToProps)(ControlPanel);
